/**
 * Render Graph 的图结构与拓扑排序（DESIGN.md §4.4）。
 *
 * 图在构建期就要拒绝非法输入。合成器每帧都跑一次，把校验放到执行期意味着
 * 错误会以"某块像素不对"的形式出现——那是最难查的一类 bug。
 * 这里的原则是：**能在加节点时发现的，绝不留到排序时；能在排序时发现的，绝不留到 GPU 上。**
 */
import { NODE_ARITY, producesDraw, type NodeId, type RenderNode } from './node';

/** 图里出现环时抛出，并指出环上的节点。 */
export class CyclicGraphError extends Error {
  constructor(readonly cycle: readonly NodeId[]) {
    // 把环路径打出来，而不只是说"有环"。合成图有几百个节点时，
    // "有环"这三个字提供的信息量约等于零
    super(`Render Graph 存在环状依赖：${cycle.join(' → ')} → ${cycle[0] ?? '?'}`);
    this.name = 'CyclicGraphError';
  }
}

/** 节点引用了不存在的上游、或输入个数不对时抛出。 */
export class InvalidGraphError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidGraphError';
  }
}

export class RenderGraph {
  readonly #nodes = new Map<NodeId, RenderNode>();

  /**
   * 加入一个节点。
   *
   * 允许上游尚未加入——图可以自底向上或自顶向下地构建，强求顺序会让调用方
   * 不得不先做一次拓扑排序才能建图。悬空引用留到 {@link topologicalOrder} 统一检查。
   */
  add(node: RenderNode): this {
    if (this.#nodes.has(node.id)) {
      throw new InvalidGraphError(`节点 id 重复：${node.id}`);
    }
    const expected = NODE_ARITY[node.kind];
    if (node.inputs.length !== expected) {
      throw new InvalidGraphError(
        `${node.kind} 节点「${node.id}」需要 ${expected} 个输入，收到 ${node.inputs.length} 个。`,
      );
    }
    if (node.kind === 'Blend' && (node.opacity < 0 || node.opacity > 1)) {
      throw new InvalidGraphError(`Blend 节点「${node.id}」的 opacity 必须在 0–1，收到 ${node.opacity}。`);
    }
    this.#nodes.set(node.id, node);
    return this;
  }

  get size(): number {
    return this.#nodes.size;
  }

  get nodes(): readonly RenderNode[] {
    return [...this.#nodes.values()];
  }

  get(id: NodeId): RenderNode | null {
    return this.#nodes.get(id) ?? null;
  }

  /** 每渲染一块需要提交多少次 draw。SourceTile 不算——它只是绑纹理。 */
  get drawsPerTile(): number {
    let n = 0;
    for (const node of this.#nodes.values()) if (producesDraw(node.kind)) n++;
    return n;
  }

  /** 唯一的输出节点。没有或多于一个都是构图错误。 */
  outputNode(): RenderNode {
    const outputs = this.nodes.filter((n) => n.kind === 'Output');
    if (outputs.length !== 1) {
      throw new InvalidGraphError(`图必须恰好有一个 Output 节点，找到 ${outputs.length} 个。`);
    }
    return outputs[0]!;
  }

  /**
   * 拓扑排序：上游一定排在下游之前。
   *
   * 用 Kahn 算法而非 DFS，因为出现环时它天然剩下"还没排掉的那些节点"，
   * 从中提取具体环路比在 DFS 的调用栈里回溯更直接。
   *
   * 结果是**确定的**：入度归零的节点按加入顺序出队。同一张图每帧排出同样的顺序，
   * 否则渲染结果会随排序抖动，像素对拍测试会变成随机红。
   */
  topologicalOrder(): readonly RenderNode[] {
    const indegree = new Map<NodeId, number>();
    const consumers = new Map<NodeId, NodeId[]>();

    for (const node of this.#nodes.values()) {
      indegree.set(node.id, node.inputs.length);
      for (const input of node.inputs) {
        if (!this.#nodes.has(input)) {
          throw new InvalidGraphError(`节点「${node.id}」引用了不存在的上游「${input}」。`);
        }
        const list = consumers.get(input);
        if (list === undefined) consumers.set(input, [node.id]);
        else list.push(node.id);
      }
    }

    // 按插入顺序初始化队列，保证排序结果稳定
    const queue: NodeId[] = [];
    for (const node of this.#nodes.values()) {
      if (indegree.get(node.id) === 0) queue.push(node.id);
    }

    const ordered: RenderNode[] = [];
    for (let head = 0; head < queue.length; head++) {
      const id = queue[head]!;
      ordered.push(this.#nodes.get(id)!);
      for (const consumer of consumers.get(id) ?? []) {
        const left = indegree.get(consumer)! - 1;
        indegree.set(consumer, left);
        if (left === 0) queue.push(consumer);
      }
    }

    if (ordered.length !== this.#nodes.size) {
      throw new CyclicGraphError(this.#findCycle(indegree));
    }
    return ordered;
  }

  /**
   * 从排序剩下的节点里提取一条真正的环。
   *
   * 剩下的节点不都在环上——挂在环下游的节点入度也归不了零。所以要顺着
   * 上游走到重复为止，只截取重复点之后的那一段，那才是环本身。
   */
  #findCycle(indegree: ReadonlyMap<NodeId, number>): readonly NodeId[] {
    const remaining = new Set<NodeId>();
    for (const [id, degree] of indegree) if (degree > 0) remaining.add(id);

    const start = [...remaining][0];
    if (start === undefined) return [];

    const path: NodeId[] = [];
    const seen = new Map<NodeId, number>();
    let current: NodeId = start;

    for (;;) {
      const at = seen.get(current);
      // 走的是上游方向，报错信息要按数据流方向读，所以倒过来
      if (at !== undefined) return path.slice(at).reverse();
      seen.set(current, path.length);
      path.push(current);

      // 沿着仍在环里的上游继续走
      const node = this.#nodes.get(current)!;
      const next = node.inputs.find((input) => remaining.has(input));
      if (next === undefined) return path.reverse();
      current = next;
    }
  }
}
